"use client";

import React from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../form.js";
import { Input } from "../../../input.js";
import { cn } from "../../../../lib/utils.js";

/**
 * react-hook-form-controlled start/end datetime pair. Stores `Date | null` in
 * form state; renders a `datetime-local` input in the user's local wall-clock.
 *
 * `stacked` renders the two fields in a single column (for the narrow sidebar);
 * otherwise they sit side-by-side from sm+.
 */

/** Date -> value for a `datetime-local` input, in local wall-clock time. */
function toInputValue(value: unknown): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(d.getTime())) return "";
  const localMs = d.getTime() - d.getTimezoneOffset() * 60000;
  return new Date(localMs).toISOString().slice(0, 16);
}

interface RHFDateTimeFieldsProps<T extends FieldValues> {
  control: Control<T>;
  startName: Path<T>;
  endName: Path<T>;
  startLabel?: string;
  endLabel?: string;
  /** Single-column layout (for the sidebar). Default false = side-by-side on sm+. */
  stacked?: boolean;
  disabled?: boolean;
  className?: string;
}

export function RHFDateTimeFields<T extends FieldValues>({
  control,
  startName,
  endName,
  startLabel = "Starts",
  endLabel = "Ends",
  stacked = false,
  disabled,
  className,
}: RHFDateTimeFieldsProps<T>) {
  return (
    <div
      className={cn(
        "grid gap-4",
        stacked ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2",
        className,
      )}
    >
      <FormField
        control={control}
        name={startName}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{startLabel}</FormLabel>
            <FormControl>
              <Input
                type="datetime-local"
                disabled={disabled}
                value={toInputValue(field.value)}
                onBlur={field.onBlur}
                onChange={(e) =>
                  field.onChange(e.target.value ? new Date(e.target.value) : null)
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={endName}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{endLabel}</FormLabel>
            <FormControl>
              <Input
                type="datetime-local"
                disabled={disabled}
                value={toInputValue(field.value)}
                onBlur={field.onBlur}
                onChange={(e) =>
                  field.onChange(e.target.value ? new Date(e.target.value) : null)
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

export default RHFDateTimeFields;
